import { Slot } from '@radix-ui/react-slot';
import { cloneElement, forwardRef, ReactElement, ReactNode, Ref } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { AnimatedHeight } from 'v5/primitives/AnimatedHeight';
import { useUnitTransition } from './unitMotion';

export interface UnitLayoutProps {
  titleSlot: ReactNode;
  actionsSlot: ReactNode;
  /** `null`: nothing shows under the title (an empty or folded unit at rest). */
  contentSlot: ReactNode;
  /** Rendered between title and content (inheritance hints). */
  aboveContentSlot: ReactNode;
  /** Rendered after content (save errors). */
  belowContentSlot: ReactNode;
  isEditing: boolean;
  submitState: 'success' | 'error' | null;
  interactive: boolean;
  /** Framed for a moment at rest: a jump landed on a unit that doesn't open. */
  revealed?: boolean;
  /**
   * Wraps the content inside the unit's animated box, e.g. in a focus trap
   * while editing. Switching the wrapper remounts only the content: the card
   * stays and eases between modes.
   */
  contentWrapper?(content: ReactElement): ReactElement;
  /** The title row: sticky while editing, where its top is the top of the view. */
  headerRef?: Ref<HTMLDivElement>;
  /**
   * An entry of a list: at rest it shows no title row, its actions float
   * over its top right corner, so it reads like the rest of the list.
   */
  floatingActionsAtRest?: boolean;
  /**
   * With `floatingActionsAtRest`: the actions stand beside the content's first line instead of
   * over its corner, so content that wraps (chips) keeps clear of them.
   */
  actionsBesideContent?: boolean;
  /**
   * While editing there is no title row: the actions stand in a tab over the frame's top right
   * corner, the field alone inside the frame (a note edited over a card).
   */
  actionsTab?: boolean;
  /**
   * The title row sticks at rest too (a group opened from its title), not
   * only while editing. It sticks `--unit-sticky-top` below the top of the
   * scroll area (0 unless a sticky row above sets it).
   */
  stickyTitle?: boolean;
  /**
   * At rest the title row adds only the title's height (its buttons reach
   * into the card's padding); the host spaces units with a gap.
   */
  compactTitleRow?: boolean;
  children: ReactElement;
}

/**
 * Visual shell of one unit: hover border, success/error tint, actions
 * revealed on hover/focus (always on touch) beside the title. Forwards its ref to the rendered
 * child.
 */
export const UnitLayout = forwardRef<HTMLElement, UnitLayoutProps>(
  function UnitLayout(
    {
      titleSlot,
      actionsSlot,
      contentSlot,
      aboveContentSlot,
      belowContentSlot,
      isEditing,
      submitState,
      interactive,
      revealed = false,
      contentWrapper = content => content,
      headerRef,
      floatingActionsAtRest = false,
      actionsBesideContent = false,
      actionsTab = false,
      stickyTitle = false,
      compactTitleRow = false,
      children
    },
    ref
  ) {
    const transition = useUnitTransition();
    const editingBackground =
      submitState === 'error' ? 'bg-bg-danger-2' : 'bg-bg-default';
    const floating = floatingActionsAtRest && !isEditing;
    const beside = floating && actionsBesideContent;
    const sticky = isEditing || stickyTitle;
    const tab = actionsTab && isEditing;

    const actions = (
      <div
        className={cn(
          'flex shrink-0 gap-0.5 transition-opacity duration-200',
          // Floating over the entry's top line (a rate's amount): the
          // card's hover background behind them, faded in from the left,
          // so the text under them doesn't show through.
          floating &&
            !beside &&
            'relative -my-1 rounded-md bg-bg-default py-1 before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-6 before:bg-gradient-to-l before:from-bg-default before:to-transparent',
          isEditing
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 has-aria-expanded:opacity-100 pointer-coarse:opacity-100'
        )}
      >
        {actionsSlot}
      </div>
    );

    const content = (
      <>
        {!tab && (
          <div
            ref={headerRef}
            className={cn(
              // Room below for the first field's focus ring (2px + offset): the
              // row, with Save in it, sits above the content.
              'flex items-center justify-between gap-4 pb-1',
              // Buttons reach into the card's padding; the title stays put
              // between modes. An entry keeps its own frame.
              compactTitleRow &&
                !floatingActionsAtRest &&
                cn(
                  '-mt-1.5 min-h-9',
                  !isEditing && contentSlot === null && '-mb-1.5 pb-0'
                ),
              floating && 'absolute top-1 right-1 z-1 pb-0',
              // While editing, Save/Cancel stay in view as a long editor (a list
              // of rates) scrolls: the row sticks to the top of the scroll area.
              // It reaches over the card's padding at the sides (its margins
              // cancel that: nothing moves) and paints the card's background, so
              // content passes under it. Before it sticks that background is the
              // card's own, so it shows only once stuck. It does not wait for
              // the `stuck-top` query, which Safari and Firefox lack: there the
              // row would stay see-through over the content.
              sticky &&
                cn(
                  'sticky top-[var(--unit-sticky-top,0px)] z-1 -mx-2 px-2 @container-[scroll-state]',
                  // It starts close to the card's border: rounded as the card,
                  // its background stays inside the corners.
                  compactTitleRow && 'rounded-t-[calc(var(--radius-lg)-1px)]',
                  'transition-[background-color] duration-200 ease-out motion-reduce:transition-none',
                  editingBackground
                )
            )}
          >
            {sticky && (
              // The shadow under the stuck row: only where `stuck-top` works.
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-full h-1.5 bg-gradient-to-b from-black/5 to-transparent opacity-0 transition-opacity duration-100 stuck-top:opacity-100"
              />
            )}
            {!floating && <div className="min-w-0">{titleSlot}</div>}
            {!beside && actions}
          </div>
        )}
        {aboveContentSlot}
        {actionsBesideContent && floatingActionsAtRest ? (
          // Its first line as tall as the buttons (a control of size sm): their middles meet.
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1">{contentSlot}</div>
            {beside && actions}
          </div>
        ) : (
          contentSlot
        )}
        {belowContentSlot}
      </>
    );

    return (
      <Slot
        ref={ref}
        data-unit-editing={isEditing || undefined}
        className={cn(
          // The card reaches past its content by 0.5rem at the sides and
          // 0.25rem above and below, the same frame on hover and in edit mode.
          // Its margin cancels its padding, so the content stays put. Units
          // sit 0.5rem apart, so two frames meet edge to edge: in edit mode
          // the card keeps its margin on the sides that have a neighbour and
          // eases away from it, so a hovered neighbour's frame doesn't touch
          // it. Content sits at least the gutter + the section's `px-2`
          // (1rem) inside the section's clipping box, so the bleed stays in
          // view.
          'group relative -mx-2 -my-1 rounded-lg border border-transparent px-2 py-1',
          compactTitleRow &&
            cn(
              '-my-2.5 py-2.5',
              // Its frame reaches into the gap: shown, it goes over the
              // neighbours (a group's title row has a background of its own).
              isEditing ? 'z-3' : interactive && 'hocus:z-3'
            ),
          // An entry has no title row above its content: as much room above
          // it as below, and its floating actions keep off the border.
          floatingActionsAtRest && '-my-2 py-2',
          isEditing && 'not-first:mt-0 not-last:mb-0',
          // the tab stands on its top right corner
          tab && 'rounded-tr-none',
          'transition-[border-color,background-color,box-shadow,margin] duration-200 ease-out motion-reduce:transition-none',
          isEditing
            ? cn(
                submitState === 'error'
                  ? 'border-border-danger-emphasis'
                  : 'border-border-default',
                editingBackground
              )
            : interactive && 'hocus:border-border-default hocus:bg-bg-default',
          submitState === 'success' && 'bg-bg-success-emphasis/5',
          revealed && !isEditing && 'border-border-accent bg-bg-accent/10'
        )}
        data-revealed={revealed || undefined}
      >
        {cloneElement(children, {
          children: (
            <>
              {tab && (
                // Outside the animated box, which would clip it while it eases in. It stands on the
                // frame's top border and covers it with its own background: one outline around the
                // frame and the tab, as a folder's.
                <div
                  className={cn(
                    'absolute top-0 -right-px z-1 flex -translate-y-full gap-0.5 rounded-t-md border border-b-0 border-border-default p-1',
                    editingBackground
                  )}
                >
                  {actionsSlot}
                </div>
              )}
              {/* The content's height eases too (display ⇄ edit, errors, a
                  list's rows, the fees breakdown); it clips only while it moves,
                  so focus rings show at rest. Meanwhile it clips at the card's
                  border (room as wide as the card's padding): a field's focus
                  ring (2px + 2px offset) stays whole, and a list row's band,
                  which reaches the border, does not widen once it settles. A
                  compact title row's buttons reach above the content: room there
                  too. */}
              <AnimatedHeight
                clip="while-resizing"
                transition={transition}
                className={cn('-mx-2', compactTitleRow && '-my-1.5')}
                contentClassName={cn('px-2', compactTitleRow && 'py-1.5')}
              >
                {contentWrapper(<div className="flow-root">{content}</div>)}
              </AnimatedHeight>
            </>
          )
        })}
      </Slot>
    );
  }
);
